/*
 * Copyright (c) 2019 Frontier Developments PLC.
 */
import {Vue} from 'vue-property-decorator';
import {MutationTree} from 'vuex';
import {JsonLdCollection} from '@/store/types';
import moment from 'moment';
import {CommunityChallengesStateInterface} from '@/store/modules/community-challenges/types';
import {CommunityChallengeInterface} from '@/store/modules/community-challenge/types';
import {RewardAchievementSet, TrophyGrade} from '@/store/modules/trophy-cabinet/types';

/**
 * Community challenges mutations.
 * @author Jonny Cook <jcook@frontier.co.uk>
 */

export const mutations: MutationTree<CommunityChallengesStateInterface> = {
    /**
     * @param {CommunityChallengesStateInterface} state
     */
    start(state: CommunityChallengesStateInterface): void {
        Vue.set(state.loadStatus, 'loading', true);
        Vue.set(state.loadStatus, 'error', false);
        Vue.set(state.loadStatus, 'success', false);
    },

    /**
     * @param {CommunityChallengesStateInterface} state
     * @param {boolean} success
     */
    stop(state: CommunityChallengesStateInterface, success: boolean = true): void {
        Vue.set(state.loadStatus, 'loading', false);
        Vue.set(state.loadStatus, 'error', !success);
        Vue.set(state.loadStatus, 'success', success);
    },

    /**
     * @param {CommunityChallengesStateInterface} state
     * @param {JsonLdCollection<CommunityChallengeInterface>} challenges
     */
    setChallenges(
        state: CommunityChallengesStateInterface,
        challenges: JsonLdCollection<CommunityChallengeInterface>,
    ): void {
        Vue.set(state, 'pagination', {
            totalItems: +challenges['hydra:totalItems'],
            current: challenges['hydra:view'] ? challenges['hydra:view']['@id'] : undefined,
            first: challenges['hydra:view'] ? challenges['hydra:view']['hydra:first'] : undefined,
            next: challenges['hydra:view'] ? challenges['hydra:view']['hydra:next'] : undefined,
            previous: challenges['hydra:view'] ? challenges['hydra:view']['hydra:previous'] : undefined,
            last: challenges['hydra:view'] ? challenges['hydra:view']['hydra:last'] : undefined,
        });
        Vue.set(state, 'challenges', challenges['hydra:member']);
        Vue.set(state, 'timestamp', moment().unix());
    },

    /**
     * @param {CommunityChallengesStateInterface} state
     */
    reset(state: CommunityChallengesStateInterface): void {
        Vue.set(state, 'challenges', []);
        Vue.set(state, 'timestamp', 0);
        Vue.set(state, 'loadStatus', {loading: false, error: false, success: false});
        Vue.set(state, 'pagination',
            {totalItems: 0, current: '', first: '', next: '', previous: '', last: ''});
    },

    /**
     * Set the contribution and trophy grade for a challenge.
     * @param {CommunityChallengesStateInterface} state
     * @param {CommunityChallengeInterface} challenge
     * @param {number} contribution
     * @param {TrophyGrade} trophy
     * @param {RewardAchievementSet} rewards
     * @param {number} percentileContribution
     */
    setContributionAndRewards(
        state: CommunityChallengesStateInterface,
        {challenge, contribution, trophy, rewards, percentileContribution}: {
            challenge: CommunityChallengeInterface,
            contribution: number,
            trophy: TrophyGrade,
            rewards: RewardAchievementSet,
            percentileContribution: number,
    }): void {
        Vue.set(challenge, 'contribution', contribution);
        Vue.set(challenge, 'trophy', trophy);
        Vue.set(challenge, 'rewards', rewards);
        Vue.set(challenge, 'percentileContribution', percentileContribution);
    },
};
